import React, { useState, useEffect, useCallback } from 'react';
import AuthTitleBar from './AuthTitleBar';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../config/supabase';
import Loading from './Loading';

// Check if running in Electron
const isElectron = Boolean(window.electronAPI);

const Login: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthPending, setOauthPending] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();

  // Handle OAuth with system browser (for Electron)
  const handleOAuthLogin = useCallback(async (provider: 'google' | 'facebook' | 'discord') => {
    try {
      setOauthPending(true);
      setError('');

      // Determine the redirect URL (frontend, not backend)
      const baseUrl = isElectron
        ? 'http://localhost:3001' // Electron always uses frontend dev server
        : window.location.origin;
      // Add electron=true parameter so callback knows to generate deep link
      const redirectUrl = isElectron
        ? `${baseUrl}/auth/callback?electron=true`
        : `${baseUrl}/auth/callback`;

      // Get the OAuth URL from Supabase
      const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          skipBrowserRedirect: isElectron, // Don't auto-redirect in Electron
          redirectTo: redirectUrl, // Redirect to frontend after auth
          queryParams: provider === 'google' ? { prompt: 'select_account' } : undefined,
        }
      });

      if (oauthError) {
        throw oauthError;
      }

      // In Electron, open the URL in system browser
      if (isElectron && data?.url) {
        // Set marker so callback page knows this came from Electron
        localStorage.setItem('oauth_from_electron', 'true');
        await window.electronAPI?.openOAuthUrl(data.url);
        // Show message to user
        setOauthPending(true);
      }
    } catch (err) {
      console.error('OAuth error:', err);
      setError(t('auth.error_login_failed'));
      setOauthPending(false);
    }
  }, [t]);

  // Listen for OAuth callback from Electron deep link
  useEffect(() => {
    if (!isElectron || !oauthPending) return;

    // Register callback for deep link tokens
    window.electronAPI?.onOAuthCallback(async (url: string) => {
      console.log('[OAuth] Deep link callback received:', url);

      try {
        // Parse tokens from deep link URL
        const urlObj = new URL(url);
        const accessToken = urlObj.searchParams.get('access_token');
        const refreshToken = urlObj.searchParams.get('refresh_token');

        if (accessToken && refreshToken) {
          console.log('[OAuth] Tokens received, storing directly in localStorage');

          // Store tokens directly in localStorage for Supabase to pick up
          // This avoids CORS issues by not making network calls to Supabase
          const supabaseKey = 'sb-wsshqqywqxfwdkyucsum-auth-token';
          const sessionData = {
            access_token: accessToken,
            refresh_token: refreshToken,
            token_type: 'bearer',
            expires_in: 3600,
            expires_at: Math.floor(Date.now() / 1000) + 3600,
          };
          localStorage.setItem(supabaseKey, JSON.stringify(sessionData));

          // Also set the access token for API calls
          const { setAccessToken } = await import('../utils/api');
          setAccessToken(accessToken);

          console.log('[OAuth] Session stored, reloading page to apply');
          setOauthPending(false);

          // Reload the page to let AuthContext pick up the new session
          window.location.href = '/';
        }
      } catch (err) {
        console.error('[OAuth] Error processing deep link:', err);
        setError('Giriş işlenirken bir hata oluştu.');
      }
    });
  }, [oauthPending]);

  // Check URL parameter for register mode
  useEffect(() => {
    const registerParam = searchParams.get('register');
    if (registerParam === 'true') {
      setIsRegistering(true);
    }
  }, [searchParams]);

  // Check for OAuth errors in URL hash (e.g., from failed Google login)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      const errorCode = params.get('error');
      const errorDescription = params.get('error_description');

      if (errorCode) {
        console.error('OAuth error:', errorCode, errorDescription);
        const friendlyMessage = errorDescription
          ? decodeURIComponent(errorDescription.replace(/\+/g, ' '))
          : t('auth.error_login_failed');
        setError(friendlyMessage);
        setOauthPending(false);
        // Clear the hash to prevent showing error on refresh
        window.history.replaceState(null, '', window.location.pathname);
      }

      // Check for successful OAuth callback
      const accessToken = params.get('access_token');
      if (accessToken) {
        setOauthPending(false);
      }
    }
  }, [t]);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Simple validation
    if (!email || !password) {
      setError(t('auth.error_fill_all'));
      return;
    }

    if (isRegistering && !username) {
      setError(t('auth.error_username_required'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (isRegistering) {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              username,
              displayName: username,
            },
          },
        });

        if (error) throw error;

        // Keep the experience the same—prompt the user to log in with the new credentials.
        alert(t('auth.success_register'));
        setIsRegistering(false);
        setUsername('');
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        // AuthContext will handle the state update via onAuthStateChange
        navigate('/');
      }
    } catch (err) {
      console.error('Authentication error:', err);
      const serverMessage = (err as Error).message;

      // Translate common error messages to user-friendly Turkish
      let friendlyMessage: string;
      if (serverMessage?.toLowerCase().includes('invalid login credentials')) {
        friendlyMessage = t('auth.error_invalid_credentials');
      } else if (serverMessage?.toLowerCase().includes('email not confirmed')) {
        friendlyMessage = t('auth.error_email_not_confirmed');
      } else {
        friendlyMessage = isRegistering
          ? t('auth.error_register_failed')
          : t('auth.error_login_failed');
      }

      setError(friendlyMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading message={isRegistering ? t('auth.register.submit') + "..." : t('auth.login.submit') + "..."} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex flex-col items-center justify-center p-4 relative">
      <div className='absolute top-0 right-0 z-20 w-auto'>
        <AuthTitleBar />
      </div>
      <div className="bg-white dark:bg-gradient-to-br dark:from-gray-800 dark:to-gray-900 rounded-2xl shadow-2xl p-8 w-full max-w-md border border-gray-200 dark:border-gray-700 mt-4 relative">
        {isRegistering && (
          <button
            onClick={() => {
              setIsRegistering(false);
              setError('');
            }}
            className="absolute top-4 left-4 text-gray-400 hover:text-white transition p-2 hover:bg-white/10 rounded-full"
            title={t('auth.go_back')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
        )}
        <div className="text-center mb-8">
          <h1
            className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-2 cursor-pointer"
            onClick={() => navigate('/')}
            title={t('server.details.back_home')}
          >
            ServeRS
          </h1>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isRegistering ? t('auth.register.title') : t('auth.login.title')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {isRegistering ? t('auth.register.subtitle') : t('auth.login.subtitle')}
          </p>
        </div>

        {error && (
          <div className="bg-red-900/50 text-red-200 p-4 rounded-xl mb-6 border border-red-800">
            <p className="font-medium">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-5">
            <label htmlFor="email" className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">{t('auth.email')}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white"
              placeholder="your@email.com"
            />
          </div>

          <div className="mb-5">
            <label htmlFor="password" className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">{t('auth.password')}</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white"
              placeholder="••••••••"
            />
          </div>

          {isRegistering && (
            <div className="mb-6">
              <label htmlFor="username" className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">{t('auth.username')}</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => { setUsername(e.target.value); setError(''); }}
                className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white"
                placeholder={t('auth.username.placeholder')}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:transform-none"
          >
            {isRegistering ? t('auth.register.submit') : t('auth.login.submit')}
          </button>
        </form>

        <div className="my-6 flex items-center">
          <div className="flex-grow border-t border-gray-600"></div>
          <span className="flex-shrink-0 mx-4 text-gray-400 text-sm">{t('auth.or_continue_with')}</span>
          <div className="flex-grow border-t border-gray-600"></div>
        </div>

        <div className="flex justify-center mb-6">
          <button
            type="button"
            onClick={() => handleOAuthLogin('google')}
            disabled={oauthPending}
            className="flex items-center justify-center gap-3 py-2.5 px-6 bg-white hover:bg-gray-100 text-gray-800 rounded-xl transition-colors duration-300 disabled:opacity-50 shadow-md"
            title={t('auth.login_with_google')}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" /></svg>
            <span>{t('auth.login_with_google')}</span>
          </button>
        </div>

        {/* OAuth pending message */}
        {oauthPending && (
          <div className="mb-6 p-4 bg-blue-500/20 border border-blue-500/50 rounded-xl text-center space-y-3">
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-400"></div>
              <p className="text-blue-300 text-sm">
                Tarayıcıda giriş yapın...
              </p>
            </div>
            <p className="text-gray-400 text-xs">
              Giriş yaptıktan sonra otomatik olarak yönlendirileceksiniz.
            </p>
            <div className="flex gap-2 justify-center mt-2">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition"
              >
                Sayfayı Yenile
              </button>
              <button
                onClick={() => setOauthPending(false)}
                className="px-4 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded-lg transition"
              >
                İptal
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 text-center space-y-4">
          <button
            onClick={() => {
              setIsRegistering(!isRegistering);
              setError('');
            }}
            className="text-blue-400 hover:text-blue-300 transition font-medium block w-full"
          >
            {isRegistering
              ? t('auth.already_have_account')
              : t('auth.no_account')}
          </button>

          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white transition font-medium flex items-center justify-center gap-2 w-full"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t('auth.go_back')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;